import { Text, Card, Box, Flex } from '@sequencehq/core-components'
import { CustomerPreviewCardPill } from 'Customer/components/CustomerPreviewCard'
import { Address } from '@sequencehq/api/utils/commonModels'
import { formatAddress } from '@sequencehq/utils'
import {
  GreyGrey60,
  GreyGrey80,
  GreyGrey90,
  IndigoIndigo60,
  Link,
  Primary13Regular,
  Primary13SemiBold
} from '@sequencehq/core-components'
import { format } from '@sequencehq/utils/dates'
import { SummaryRow } from './SummaryRow'
import { CreditNoteBillingPeriod } from './CreditNoteBillingPeriod'
import { CreditNoteAccountingDate } from './CreditNoteAccountingDate'
import { CreditNoteTaxDate } from './CreditNoteTaxDate'
import { AggregatedCreditNote } from 'CreditNotes/types'
import { useTaxIntegrationProvider } from 'lib/hooks/useTaxIntegrationProvider'
import { useQuery } from '@sequencehq/api/utils'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { NO_ADDRESS_LABEL, taxStatusOptions } from 'modules/Customers/data'

interface Props {
  customerId: string
  customerEmails: string[]
  customerAddress: Address | null
  creditNoteNumber?: string
  creditNoteIssueDate?: string
  currency: string
  customerTaxId?: string
  invoiceNumber?: string
  invoiceId?: string
  billingPeriodStart: AggregatedCreditNote['billingPeriodStart']
  billingPeriodEnd: AggregatedCreditNote['billingPeriodEnd']
  accountingDate?: AggregatedCreditNote['accountingDate']
  taxDate?: AggregatedCreditNote['taxDate']
  updateBillingPeriod: ({
    billingPeriodStart,
    billingPeriodEnd
  }: {
    billingPeriodStart: AggregatedCreditNote['billingPeriodStart']
    billingPeriodEnd: AggregatedCreditNote['billingPeriodEnd']
  }) => void
  updateAccountingDate: (
    accountingDate: AggregatedCreditNote['accountingDate']
  ) => void
  updateTaxDate: (taxDate: AggregatedCreditNote['taxDate']) => void
  status: AggregatedCreditNote['status']
}

export const CreditNoteDetailsSummaryCard = ({
  customerId,
  creditNoteIssueDate,
  creditNoteNumber,
  currency,
  customerEmails,
  customerAddress,
  customerTaxId,
  invoiceNumber,
  invoiceId,
  billingPeriodStart,
  billingPeriodEnd,
  accountingDate,
  taxDate,
  updateBillingPeriod,
  updateAccountingDate,
  updateTaxDate,
  status
}: Props) => {
  const { isAnrok } = useTaxIntegrationProvider()
  const customerTaxStatusQuery = useQuery(
    dashboardv99990101Client.getCustomer,
    { id: customerId },
    { select: customer => customer?.taxStatus }
  )
  const customerTaxStatus = customerTaxStatusQuery.data
  return (
    <Card padding="12px 16px" variant="v2" data-testid="creditNote.summaryCard">
      <Flex gap={4}>
        <Flex width="50%">
          <Flex
            alignContent="center"
            alignItems="center"
            width="140px"
            height="28px"
          >
            <Text {...Primary13Regular} color={GreyGrey60}>
              Credited to
            </Text>
          </Flex>
          <Flex flexDirection="column" gap={1} {...Primary13Regular}>
            <Box mx={-2}>
              <CustomerPreviewCardPill
                customerId={customerId}
                variant="ghost"
              />
            </Box>
            <Text {...Primary13Regular} color={GreyGrey80}>
              {customerEmails[0]}
            </Text>
            <Flex
              flexDirection="column"
              gap={1}
              data-testid="creditNote.customerAddress"
            >
              {customerAddress ? (
                formatAddress(customerAddress).map((textValue, index) => (
                  <Text
                    {...Primary13Regular}
                    color={GreyGrey80}
                    key={`${index}-${textValue}`}
                    style={{
                      overflowWrap: 'anywhere'
                    }}
                  >
                    {textValue}
                  </Text>
                ))
              ) : (
                <Text {...Primary13Regular} color={GreyGrey60}>
                  {NO_ADDRESS_LABEL}
                </Text>
              )}
            </Flex>
          </Flex>
        </Flex>

        <Flex flexDirection="column" width="50%">
          <SummaryRow
            label="Credit note number"
            value={
              <Text
                {...Primary13Regular}
                color={creditNoteNumber ? GreyGrey90 : GreyGrey60}
              >
                {creditNoteNumber || 'Pending'}
              </Text>
            }
          />
          {!!invoiceId && (
            <SummaryRow
              label="Related to"
              value={
                <Link.Unstyled to={`/invoices/${invoiceId}`}>
                  <Text {...Primary13SemiBold} color={IndigoIndigo60}>
                    {invoiceNumber ?? 'view invoice'}
                  </Text>
                </Link.Unstyled>
              }
            />
          )}
          <CreditNoteBillingPeriod
            updateBillingPeriod={updateBillingPeriod}
            billingPeriodStart={billingPeriodStart}
            billingPeriodEnd={billingPeriodEnd}
            status={status}
          />
          <CreditNoteAccountingDate
            updateAccountingDate={updateAccountingDate}
            accountingDate={accountingDate}
            status={status}
          />
          {isAnrok && (
            <CreditNoteTaxDate
              updateTaxDate={updateTaxDate}
              taxDate={taxDate}
              status={status}
            />
          )}
          <SummaryRow
            label="Issue date"
            value={
              <Text {...Primary13Regular}>
                {creditNoteIssueDate
                  ? format(new Date(creditNoteIssueDate), 'd MMM yyyy')
                  : '-'}
              </Text>
            }
          />
          <SummaryRow
            label="Currency"
            value={<Text {...Primary13Regular}>{currency}</Text>}
          />
          <SummaryRow
            label="Tax status"
            value={
              <Text
                {...Primary13Regular}
                color={customerTaxStatus ? GreyGrey90 : GreyGrey60}
                data-testid="creditNote.summary.taxStatus"
              >
                {customerTaxStatus ? taxStatusOptions[customerTaxStatus] : '-'}
              </Text>
            }
          />
          {customerTaxId && (
            <SummaryRow
              label="Tax ID"
              value={
                <Text
                  {...Primary13Regular}
                  data-testid="creditNote.summary.taxId"
                >
                  {customerTaxId}
                </Text>
              }
            />
          )}
        </Flex>
      </Flex>
    </Card>
  )
}
